import { useTranslate, type Identifier } from "ra-core";
import { useState } from "react";
import { CloudOff, Play, Square } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { PriorityBadge } from "../projects/views/PriorityBadge";
import { statusColor } from "../projects/views/statusColors";
import type { Sale, Section, WorkItem, WorkItemPriority } from "../types";
import { entrySeconds, formatClock, formatDuration } from "./timeFormat";
import type { useTimer } from "./useTimer";
import { formatDateShort } from "../misc/osloDate";

type Timer = ReturnType<typeof useTimer>;

/**
 * Inline-redigerbare celler for oppgaverader.
 *
 * Cellene lå opprinnelig private i ProjectListView. De er flyttet hit fordi den
 * globale oppgavelista skal se ut og oppføre seg likt — ett sett celler, to
 * flater, slik at redigering ikke kan drifte fra hverandre.
 *
 * Alle lagrer umiddelbart via `onUpdate` (optimistisk oppdatering hos den som
 * kaller), så det er ingen lagre-knapp per rad.
 */

// Sentinelverdi for «ingen» i nedtrekkene (Radix Select tillater ikke "").
const NONE = "__none__";

const dash = <span className="text-muted-foreground">–</span>;

const priorities: WorkItemPriority[] = ["low", "normal", "high", "urgent"];

// Felles minimal-stil for nedtrekk som ligger rett i tabellcellen (uten ramme,
// men med hover slik at det er tydelig at man kan klikke).
const cellTrigger =
  "h-7 w-full border-0 bg-transparent px-1 shadow-none hover:bg-muted focus:ring-0 data-[size=default]:h-7";

/** Ansvarlig. */
export const AssigneeCell = ({
  item,
  sales,
  onUpdate,
}: {
  item: WorkItem;
  sales: Sale[];
  onUpdate: (data: Partial<WorkItem>) => void;
}) => (
  <Select
    value={item.assignee_id != null ? String(item.assignee_id) : NONE}
    onValueChange={(v) =>
      onUpdate({ assignee_id: v === NONE ? null : Number(v) })
    }
  >
    <SelectTrigger className={cn(cellTrigger, "text-xs text-muted-foreground")}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={NONE}>{dash}</SelectItem>
      {sales.map((s) => (
        <SelectItem key={s.id} value={String(s.id)}>
          {s.first_name} {s.last_name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** Prioritet. */
export const PriorityCell = ({
  item,
  onUpdate,
}: {
  item: WorkItem;
  onUpdate: (data: Partial<WorkItem>) => void;
}) => (
  <Select
    value={item.priority ?? NONE}
    onValueChange={(v) =>
      onUpdate({ priority: v === NONE ? null : (v as WorkItemPriority) })
    }
  >
    <SelectTrigger className={cellTrigger}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={NONE}>{dash}</SelectItem>
      {priorities.map((p) => (
        <SelectItem key={p} value={p}>
          <PriorityBadge priority={p} />
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Status = seksjonen oppgaven ligger i (samme begrep som kolonnene på tavla).
 *
 * En seksjon hører til **ett** scope: enten et prosjekt, eller den globale
 * lista (`project_id` null) som oppgaver på kundenivå bruker. Valgene må
 * begrenses til oppgavens eget scope — ellers kunne en oppgave havne i en
 * seksjon som hører et annet sted, og forsvinne fra begge tavlene.
 * Databasetriggeren nuller da statusen, så feilen ville vist seg som at
 * valget «ikke tok».
 */
export const StatusCell = ({
  item,
  sections,
  onUpdate,
}: {
  item: WorkItem;
  /** Alle seksjoner; filtreres her på oppgavens scope. */
  sections: Section[];
  onUpdate: (data: Partial<WorkItem>) => void;
}) => {
  const egne = sections.filter((s) =>
    item.project_id == null
      ? s.project_id == null
      : String(s.project_id) === String(item.project_id),
  );
  const valgt = egne.find((s) => String(s.id) === String(item.section_id));
  return (
    <Select
      value={item.section_id != null ? String(item.section_id) : NONE}
      onValueChange={(v) =>
        onUpdate({ section_id: v === NONE ? null : Number(v) })
      }
    >
      <SelectTrigger className={cn(cellTrigger, "text-xs")}>
        {valgt ? (
          // Farget prikk + vanlig tekstfarge. Tekst i statusfargen på en
          // tonet bakgrunn i samme farge ga for lav kontrast (grå på grått).
          <StatusLabel color={valgt.color} name={valgt.name} />
        ) : (
          <SelectValue placeholder={dash} />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{dash}</SelectItem>
        {egne.map((s) => (
          <SelectItem key={s.id} value={String(s.id)}>
            <StatusLabel color={s.color} name={s.name} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const StatusLabel = ({
  color,
  name,
}: {
  color: string | null | undefined;
  name: string;
}) => (
  <span className="flex min-w-0 items-center gap-1.5">
    <span
      aria-hidden
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: statusColor(color) }}
    />
    <span className="truncate text-foreground">{name}</span>
  </span>
);

/** Frist. Tømmes med «Fjern frist». */
export const DueDateCell = ({
  item,
  onUpdate,
}: {
  item: WorkItem;
  onUpdate: (data: Partial<WorkItem>) => void;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [verdi, setVerdi] = useState("");

  const onOpenChange = (next: boolean) => {
    if (next) setVerdi(item.due_date ?? "");
    setOpen(next);
  };

  const lagre = (v: string | null) => {
    onUpdate({ due_date: v || null });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full rounded px-1 py-0.5 text-center text-xs text-muted-foreground hover:bg-muted"
        >
          {item.due_date ? formatDateShort(item.due_date) : dash}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <div className="flex items-center gap-1">
          <Input
            type="date"
            value={verdi}
            autoFocus
            onChange={(e) => setVerdi(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lagre(verdi)}
            className="h-8"
          />
          <Button size="sm" onClick={() => lagre(verdi)}>
            {translate("ra.action.save", { _: "Lagre" })}
          </Button>
        </div>
        {item.due_date && (
          <Button
            size="sm"
            variant="ghost"
            className="mt-1 h-7 w-full text-muted-foreground"
            onClick={() => lagre(null)}
          >
            {translate("crm.list.clear_due_date", { _: "Fjern frist" })}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Estimat (timer + minutter) i et lite sprett-vindu. */
export const EstimateCell = ({
  item,
  onUpdate,
}: {
  item: WorkItem;
  onUpdate: (data: Partial<WorkItem>) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState("");
  const [m, setM] = useState("");

  const onOpenChange = (next: boolean) => {
    if (next) {
      const sec = item.estimate_seconds ?? 0;
      setH(String(Math.floor(sec / 3600) || ""));
      setM(String(Math.floor((sec % 3600) / 60) || ""));
    }
    setOpen(next);
  };

  const save = () => {
    const seconds =
      (parseInt(h || "0", 10) || 0) * 3600 + (parseInt(m || "0", 10) || 0) * 60;
    onUpdate({ estimate_seconds: seconds || null });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full rounded px-1 py-0.5 text-center text-xs text-muted-foreground hover:bg-muted"
        >
          {item.estimate_seconds ? formatDuration(item.estimate_seconds) : dash}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <TidInput h={h} m={m} setH={setH} setM={setM} onSave={save} />
      </PopoverContent>
    </Popover>
  );
};

/**
 * Registrer tid direkte i «Registrert tid»-cellen: klikk åpner et lite
 * sprett-vindu der du taster inn tid, som logges som en ny tidsoppføring
 * (ajax – lista oppdateres uten omlasting).
 */
export const TrackedCell = ({
  tracked,
  onRegister,
}: {
  tracked: number;
  onRegister: (seconds: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState("");
  const [m, setM] = useState("");

  const onOpenChange = (next: boolean) => {
    if (next) {
      setH("");
      setM("");
    }
    setOpen(next);
  };

  const save = () => {
    const seconds =
      (parseInt(h || "0", 10) || 0) * 3600 + (parseInt(m || "0", 10) || 0) * 60;
    if (seconds > 0) onRegister(seconds);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full rounded px-1 py-0.5 text-center text-xs text-muted-foreground hover:bg-muted"
        >
          {tracked > 0 ? formatDuration(tracked) : dash}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <TidInput h={h} m={m} setH={setH} setM={setM} onSave={save} />
      </PopoverContent>
    </Popover>
  );
};

// Timer + minutter + Lagre. Delt av Estimat og Registrert tid, som hadde
// identisk markup.
const TidInput = ({
  h,
  m,
  setH,
  setM,
  onSave,
}: {
  h: string;
  m: string;
  setH: (v: string) => void;
  setM: (v: string) => void;
  onSave: () => void;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        min={0}
        value={h}
        autoFocus
        onChange={(e) => setH(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSave()}
        className="h-8 w-14"
        placeholder="t"
      />
      <span className="text-xs text-muted-foreground">t</span>
      <Input
        type="number"
        min={0}
        max={59}
        value={m}
        onChange={(e) => setM(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSave()}
        className="h-8 w-14"
        placeholder="m"
      />
      <span className="text-xs text-muted-foreground">m</span>
      <Button size="sm" onClick={onSave}>
        {translate("ra.action.save", { _: "Lagre" })}
      </Button>
    </div>
  );
};

/**
 * Start/stopp-timer for en oppgave. Når timeren går på nettopp denne oppgaven
 * viser knappen en levende klokke og stopper ved klikk. Er stoppen ikke
 * bekreftet av serveren ennå, står den som «Uavklart» (PRD US06).
 */
/**
 * Timer som er stoppet lokalt, men der serveren ikke har bekreftet stoppen
 * (nettet falt ut). Klikk sender stoppen på nytt.
 */
export const UnsettledTimerButton = ({
  timer,
  className,
}: {
  timer: Timer;
  className?: string;
}) => {
  const translate = useTranslate();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void timer.retry();
      }}
      title={translate("crm.time.unsettled_hint", {
        _: "Stoppen er ikke bekreftet av serveren. Klikk for å prøve igjen.",
      })}
      className={cn(
        "flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400",
        className,
      )}
    >
      <CloudOff className="h-3 w-3" />
      {translate("crm.time.unsettled", { _: "Uavklart" })}
    </button>
  );
};

export const TimerButton = ({
  itemId,
  timer,
}: {
  itemId: Identifier;
  timer: Timer;
}) => {
  const translate = useTranslate();
  const { runningEntry, unsettledEntry, unsettledStop, nowMs } = timer;
  const runningHere =
    runningEntry != null &&
    String(runningEntry.work_item_id) === String(itemId);
  const unsettledHere =
    unsettledStop != null &&
    (unsettledEntry == null ||
      String(unsettledEntry.work_item_id) === String(itemId));

  if (unsettledHere && unsettledEntry) {
    return <UnsettledTimerButton timer={timer} />;
  }
  if (runningHere) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void timer.stop();
        }}
        disabled={timer.busy}
        aria-label={translate("crm.time.stop", { _: "Stopp timer" })}
        className="flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-mono tabular-nums text-primary hover:bg-primary/20"
      >
        <Square className="h-3 w-3 fill-current" />
        {formatClock(entrySeconds(runningEntry.started_at, null, nowMs))}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void timer.start(itemId);
      }}
      disabled={timer.busy}
      title={translate("crm.time.start", { _: "Start timer" })}
      aria-label={translate("crm.time.start", { _: "Start timer" })}
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
    >
      <Play className="h-3.5 w-3.5" />
    </button>
  );
};
